import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {Link, withRouter} from 'react-router';
import {Card, MenuListItem, Text, Banner, WithTooltip, Icon, Anchor} from '@albert-io/atomic';

import ArrayToReadableFragment from 'lib/hocs/arrayToReadableFragment';

import {coteachersHelpArticleUrl} from '../Coteachers/coteachers.utils';

import PeopleContext from '../people.context';

const enrollmentTotals = {
  invitations: null,
  students: null,
  coteachers: null
};

function getTotals({isReady, queries}) {
  if (!isReady) {
    return enrollmentTotals;
  }

  Object.keys(enrollmentTotals).forEach((key) => {
    const meta = queries[key].getResourceMetadata();
    enrollmentTotals[key] = meta.getIn(['page', 'total_entries']);
  });
  return enrollmentTotals;
}

const ItemTotal = ({children, className, selected, ...rest}) => (
  <Text
    bold
    size='s'
    className={className}
    color={selected ? 'blue' : 'black'}
    emphasis={selected ? 'high' : 'lowest'}
    {...rest}
  >
    {children}
  </Text>
);

ItemTotal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  selected: PropTypes.bool
};

const SideNav = ({location, params}) => {
  const {isReady, classroom, queries} = useContext(PeopleContext);
  const totals = getTotals({isReady, queries});
  const school = classroom.getSchool();
  const emailDomains = isReady ? school.getEmailDomains() : null;
  return (
    <>
      <Card className='manage-roster-nav u-pad_2 u-mar-b_2' shadow='default'>
        <Text as='div' className='u-pad_2 u-color_slate-500' size='m' bold>
          Students
        </Text>
        <MenuListItem
          as={Link}
          selected={location.pathname.includes('enrolled')}
          to={`/classes/${params.classId}/people/students/enrolled`}
        >
          <MenuListItem.Text>Enrolled</MenuListItem.Text>
          <ItemTotal className='u-mar-l_auto'>{totals.students}</ItemTotal>
        </MenuListItem>
        <MenuListItem
          as={Link}
          selected={location.pathname.includes('invited')}
          to={`/classes/${params.classId}/people/students/invited`}
        >
          <MenuListItem.Text>Invited</MenuListItem.Text>
          <ItemTotal className='u-mar-l_auto'>{totals.invitations}</ItemTotal>
        </MenuListItem>
        <Text as='div' className='u-pad_2 u-mar-t_2 u-color_slate-500' size='m' bold>
          Co-teachers
          <WithTooltip
            focusable
            placement='right'
            content={
              <>
                Co-teachers can invite students, make assignments, and more!&nbsp;
                <Anchor
                  href={coteachersHelpArticleUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  as='a'
                >
                  Learn more
                </Anchor>
                .
              </>
            }
            wrapperProps={{className: 'u-mar-l_1 u-mar-r_auto'}}
            theme='light'
          >
            <Icon color='black' emphasis='lowest' icon={['far', 'question-circle']} />
          </WithTooltip>
        </Text>
        <MenuListItem
          as={Link}
          selected={location.pathname.includes('approved')}
          to={`/classes/${params.classId}/people/coteachers/approved`}
        >
          <MenuListItem.Text>Approved</MenuListItem.Text>
          <ItemTotal className='u-mar-l_auto'>{totals.coteachers}</ItemTotal>
        </MenuListItem>
      </Card>
      {emailDomains && emailDomains.size > 0 && (
        <Banner>
          <Banner.Icon icon={['far', 'school']} />
          <div className='u-mar-r_1'>
            Accounts ending in{' '}
            <ArrayToReadableFragment
              arr={[...emailDomains.map((domain) => domain.getName())]}
              as='strong'
            />{' '}
            are managed by {school.getName()}.
          </div>
          <WithTooltip
            placement='top'
            content={`If you enroll students whose accounts are managed by your school, you'll be able to reset their password directly from this page.`}
            focusable
            hideFocus
            wrapperProps={{className: 'u-mar-l_auto'}}
          >
            <Banner.Icon
              className='manage-roster__tooltip-btn'
              icon='question-circle'
              iconStyle='regular'
            />
          </WithTooltip>
        </Banner>
      )}
    </>
  );
};

SideNav.propTypes = {
  location: PropTypes.object,
  params: PropTypes.object
};

export default withRouter(SideNav);
